<template>
  <header class="site-header" :class="{ 'site-header--vecura': isVecura }">
    <div class="site-header__inner">
      <a class="site-header__brand" href="/" aria-label="Startseite">
        <BrandLogo :variant="isVecura ? 'vecura' : 'default'" />
      </a>

      <nav class="site-header__nav" aria-label="Hauptnavigation">
        <a
          v-for="item in navItems"
          :key="item.href"
          :class="{
            'site-header__link--active': isActive(item.href),
            'site-header__link--featured': item.featured,
          }"
          class="site-header__link"
          :href="item.href"
        >
          {{ item.label }}
        </a>
      </nav>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import BrandLogo from "@/components/BrandLogo.vue";

const navItems = [
  {
    label: "Profil",
    href: "/",
  },
  {
    label: "Expertise",
    href: "/#expertise",
  },
  {
    label: "Kontakt",
    href: "/#contact",
  },
  {
    label: "Vecura",
    href: "/vecura",
    featured: true,
  },
];

export default defineComponent({
  name: "SiteHeader",
  components: {
    BrandLogo,
  },
  props: {
    activeSection: {
      type: String,
      required: true,
    },
    currentPath: {
      type: String,
      required: true,
    },
    isVecura: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    function isActive(href: string): boolean {
      if (href === "/") {
        return !props.isVecura && props.activeSection === "profile";
      }

      if (href === "/vecura") {
        return props.isVecura || props.activeSection === "vecura";
      }

      if (href === "/#expertise") {
        return !props.isVecura && props.activeSection === "expertise";
      }

      if (href === "/#contact") {
        return !props.isVecura && props.activeSection === "contact";
      }

      return props.currentPath.split("#")[0].replace(/\/+$/, "") === href;
    }

    return {
      isActive,
      navItems,
    };
  },
});
</script>

<style scoped>
.site-header {
  position: sticky;
  z-index: 20;
  top: 0;
  margin-right: calc(50% - 50vw);
  margin-left: calc(50% - 50vw);
  border-bottom: 1px solid var(--header-border);
  background: var(--header-bg);
  backdrop-filter: blur(18px);
  transition: background 420ms ease, border-color 420ms ease, color 420ms ease;
}

.site-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: min(1180px, calc(100% - 40px));
  min-height: 76px;
  margin: 0 auto;
}

.site-header__brand {
  display: inline-flex;
  align-items: center;
  color: var(--header-text);
  text-decoration: none;
}

.site-header__nav {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.site-header__link {
  display: inline-flex;
  align-items: center;
  border: 1px solid transparent;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  color: var(--header-muted);
  font-size: 0.92rem;
  font-weight: 800;
  text-decoration: none;
  transition: background 220ms ease, border-color 220ms ease, color 220ms ease;
}

.site-header__link:hover,
.site-header__link--active {
  background: var(--header-active-bg);
  color: var(--header-text);
}

.site-header__link--featured {
  position: relative;
  margin-left: 8px;
  border-color: rgba(20, 20, 20, 0.16);
  background: rgba(20, 20, 20, 0.025);
  color: var(--header-text);
}

.site-header__link--featured::after {
  position: absolute;
  right: 12px;
  bottom: 6px;
  left: 12px;
  height: 2px;
  border-radius: 999px;
  background: var(--color-yellow);
  content: "";
  opacity: 0.68;
  transform: scaleX(0.58);
  transform-origin: left;
  transition: opacity 220ms ease, transform 220ms ease, background 220ms ease;
}

.site-header__link--featured:hover,
.site-header__link--featured.site-header__link--active {
  border-color: rgba(20, 20, 20, 0.26);
  background: rgba(20, 20, 20, 0.055);
}

.site-header__link--featured:hover::after,
.site-header__link--featured.site-header__link--active::after {
  opacity: 1;
  transform: scaleX(1);
}

.site-header--vecura .site-header__link--featured {
  border-color: rgba(0, 181, 237, 0.46);
  background: rgba(0, 181, 237, 0.12);
}

.site-header--vecura .site-header__link--featured::after {
  background: var(--color-vecura);
  opacity: 1;
  transform: scaleX(1);
}

.site-header--vecura .site-header__link--featured:hover,
.site-header--vecura .site-header__link--featured.site-header__link--active {
  border-color: var(--color-vecura);
  background: rgba(0, 181, 237, 0.2);
}

.site-header__brand:focus-visible,
.site-header__link:focus-visible {
  outline: 2px solid var(--color-yellow);
  outline-offset: 3px;
}

.site-header--vecura .site-header__brand:focus-visible,
.site-header--vecura .site-header__link:focus-visible {
  outline-color: var(--color-vecura);
}

@media (max-width: 700px) {
  .site-header__inner {
    align-items: center;
    gap: 14px;
    width: min(100% - 28px, 1180px);
    min-height: 66px;
    padding: 10px 0;
  }

  .site-header__nav {
    flex-wrap: nowrap;
    gap: 4px;
    max-width: calc(100vw - 110px);
    overflow-x: auto;
    scrollbar-width: none;
  }

  .site-header__nav::-webkit-scrollbar {
    display: none;
  }

  .site-header__link {
    min-height: 34px;
    padding: 0 9px;
    font-size: 0.86rem;
  }
}

@media (max-width: 440px) {
  .site-header__inner {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
  }

  .site-header__nav {
    grid-column: 1 / -1;
    justify-content: flex-start;
    max-width: 100%;
  }

  .site-header__link--featured {
    margin-left: 0;
  }
}

@media (max-width: 760px) {
  .site-header {
    z-index: 40;
  }

  .site-header__inner {
    display: flex;
    min-height: var(--site-header-height);
    width: min(100% - 28px, 1180px);
    padding: 8px 0;
  }

  .site-header__nav {
    position: fixed !important;
    z-index: 50;
    top: auto !important;
    right: 14px;
    bottom: 12px !important;
    bottom: calc(12px + env(safe-area-inset-bottom, 0px)) !important;
    left: 14px;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 5px;
    max-width: 520px;
    margin: 0 auto;
    padding: 6px;
    border: 1px solid rgba(20, 20, 20, 0.1);
    border-radius: 18px;
    background: rgba(244, 241, 234, 0.92);
    box-shadow: 0 18px 45px rgba(31, 28, 20, 0.16);
    overflow: visible;
    backdrop-filter: blur(18px);
  }

  .site-header__link {
    justify-content: center;
    min-height: 42px;
    padding: 0 6px;
    border-radius: 13px;
    font-size: 0.78rem;
    white-space: nowrap;
  }

  .site-header__link--active {
    background: var(--color-ink);
    color: #ffffff;
  }

  .site-header__link--featured {
    margin-left: 0;
    border-color: transparent;
    background: transparent;
    color: var(--header-text);
  }

  .site-header__link--featured::after {
    right: 50%;
    bottom: 5px;
    left: auto;
    width: 18px;
    opacity: 0.76;
    transform: translateX(50%) scaleX(1);
  }

  .site-header__link--featured:hover,
  .site-header__link--featured.site-header__link--active {
    border-color: transparent;
    background: var(--color-ink);
    color: #ffffff;
  }

  .site-header__link--featured:hover::after,
  .site-header__link--featured.site-header__link--active::after {
    opacity: 1;
    transform: translateX(50%) scaleX(1);
  }

  .site-header--vecura .site-header__nav {
    border-color: rgba(229, 230, 231, 0.16);
    background: rgba(14, 21, 28, 0.9);
    box-shadow: 0 18px 45px rgba(0, 0, 0, 0.34);
  }

  .site-header--vecura .site-header__link--active,
  .site-header--vecura .site-header__link--featured.site-header__link--active {
    background: rgba(0, 181, 237, 0.2);
    color: #ffffff;
  }

  .site-header--vecura .site-header__link--featured {
    border-color: transparent;
    background: transparent;
  }

  .site-header--vecura .site-header__link--featured:hover {
    background: rgba(0, 181, 237, 0.16);
  }
}
</style>
